import {getCurrentUserLogin} from '@github-ui/client-env'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {PinCommentMutationMutation} from '@github-ui/server-persisted-queries/types/pinCommentMutation'
import type {UnpinCommentMutationMutation} from '@github-ui/server-persisted-queries/types/unpinCommentMutation'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

const PIN_ERROR = 'Failed to pin comment'
const UNPIN_ERROR = 'Failed to unpin comment'
type PinIssueComment = NonNullable<NonNullable<PinCommentMutationMutation['pinIssueComment']>['issueComment']>
type UnpinIssueComment = NonNullable<NonNullable<UnpinCommentMutationMutation['unpinIssueComment']>['issueComment']>
type PinnedCommentCard = NonNullable<PinIssueComment['issue']['pinnedIssueComment']>
type PinnedComment = PinnedCommentCard['issueComment']
type OptimisticPinnedCommentCard = Omit<PinnedCommentCard, 'pinnedBy'> & {
  readonly pinnedBy: PinnedCommentCard['pinnedBy'] | null
}

export type PinnedCommentMutationOptions = {
  issueId: PinIssueComment['issue']['id'] | null
  nameWithOwner: string
  comment: Pick<PinnedComment, 'id' | 'shortBodyHTML' | 'createdAt' | 'url' | 'author'>
}

type PinningResult =
  | {
      readonly pinned: true
      readonly issueId: PinIssueComment['issue']['id']
      readonly commentId: PinIssueComment['id']
      readonly pinnedIssueComment: PinnedCommentCard
    }
  | {
      readonly pinned: false
      readonly issueId: UnpinIssueComment['issue']['id']
      readonly commentId: UnpinIssueComment['id']
      readonly pinnedIssueComment: null
    }

type GraphQLResponse<TData> = {
  readonly data?: TData | null
  readonly errors?: ReadonlyArray<{readonly message?: string}> | null
}

type PinningOperation = SetFieldsOperation & {
  pinning: {execute: () => Promise<PinningResult>}
}

async function readGraphQLResponse<TData>(response: Response, fallbackMessage: string): Promise<TData> {
  let result: GraphQLResponse<TData> | null
  try {
    result = (await response.json()) as GraphQLResponse<TData> | null
  } catch (error) {
    throw new Error(response.ok ? fallbackMessage : `${fallbackMessage} (${response.status})`, {cause: error})
  }
  const serverMessage = result?.errors?.[0]?.message
  if (!response.ok) throw new Error(serverMessage ?? `${fallbackMessage} (${response.status})`)
  if (!result?.data) throw new Error(serverMessage ?? fallbackMessage)

  return result.data
}

async function submitCommentPinningMutation({
  pinned,
  issueId,
  nameWithOwner,
  commentId,
}: {
  pinned: boolean
  issueId: string
  nameWithOwner: string
  commentId: string
}): Promise<PinningResult> {
  const fallbackMessage = pinned ? PIN_ERROR : UNPIN_ERROR
  let response: Response
  try {
    response = await reactFetchJSON(`/${nameWithOwner}/comments/pin`, {
      method: pinned ? 'PUT' : 'DELETE',
      body: {input: {issueCommentId: commentId}},
    })
  } catch (error) {
    throw new Error(fallbackMessage, {cause: error})
  }

  if (pinned) {
    const data = await readGraphQLResponse<PinCommentMutationMutation>(response, fallbackMessage)
    const issueComment = data.pinIssueComment?.issueComment
    const issue = issueComment?.issue
    const pinnedIssueComment = issue?.pinnedIssueComment

    if (
      !issueComment ||
      issueComment.id !== commentId ||
      issueComment.isPinned !== true ||
      issue?.id !== issueId ||
      !pinnedIssueComment ||
      !pinnedIssueComment.pinnedBy ||
      !pinnedIssueComment.issueComment ||
      pinnedIssueComment.issueComment.id !== commentId
    ) {
      throw new Error(fallbackMessage)
    }

    return {
      pinned: true,
      issueId: issue.id,
      commentId: issueComment.id,
      pinnedIssueComment,
    }
  }

  const data = await readGraphQLResponse<UnpinCommentMutationMutation>(response, fallbackMessage)
  const issueComment = data.unpinIssueComment?.issueComment
  const issue = issueComment?.issue
  if (
    !issueComment ||
    issueComment.id !== commentId ||
    issueComment.isPinned !== false ||
    issue?.id !== issueId ||
    issue.pinnedIssueComment !== null
  ) {
    throw new Error(fallbackMessage)
  }

  return {
    pinned: false,
    issueId: issue.id,
    commentId: issueComment.id,
    pinnedIssueComment: null,
  }
}

function commentPinningFields(result: PinningResult): Record<string, unknown> {
  if (result.pinned) {
    return {
      isPinned: true,
      viewerCanPin: false,
      viewerCanUnpin: result.pinnedIssueComment.issueComment.viewerCanUnpin,
      pinnedBy: result.pinnedIssueComment.pinnedBy,
    }
  }

  return {isPinned: false, viewerCanPin: true, viewerCanUnpin: false, pinnedBy: null}
}

function optimisticPinnedComment(
  issueId: string,
  comment: PinnedCommentMutationOptions['comment'],
): OptimisticPinnedCommentCard {
  const viewerLogin = getCurrentUserLogin()
  return {
    id: `client:pinnedIssueComment:${issueId}`,
    pinnedBy: viewerLogin
      ? {
          __typename: 'User',
          id: `client:pinnedBy:${comment.id}`,
          login: viewerLogin,
        }
      : null,
    issueComment: {...comment, viewerCanUnpin: true},
  }
}

function useCommentPinningMutation(pinned: boolean, options: PinnedCommentMutationOptions, onError: () => void) {
  const {comment, issueId, nameWithOwner} = options
  const scope = {id: `comment-pinning:${issueId ?? comment.id}`}
  const {mutate: mutateIssue} = useEntityMutation<PinningResult>({
    scope,
    mutationFn: operation => (operation as PinningOperation).pinning.execute(),
    getConfirmedOperation: result => ({
      type: 'setFields',
      id: result.issueId,
      fields: {pinnedIssueComment: result.pinnedIssueComment},
    }),
    onError,
  })
  const {mutate: mutateComment} = useEntityMutation<PinningResult, Error>({
    scope,
    mutationFn: operation => (operation as PinningOperation).pinning.execute(),
    getConfirmedOperation: result => ({
      type: 'setFields',
      id: result.commentId,
      fields: commentPinningFields(result),
    }),
  })

  return () => {
    if (!issueId) return
    let request: Promise<PinningResult> | undefined
    const execute = () =>
      (request ??= submitCommentPinningMutation({
        pinned,
        issueId,
        nameWithOwner,
        commentId: comment.id,
      }))
    const issueOperation: PinningOperation = {
      type: 'setFields',
      id: issueId,
      fields: {
        pinnedIssueComment: pinned ? optimisticPinnedComment(issueId, comment) : null,
      },
      pinning: {execute},
    }
    const pinnedIssueComment = issueOperation.fields.pinnedIssueComment as OptimisticPinnedCommentCard | null
    const commentOperation: PinningOperation = {
      type: 'setFields',
      id: comment.id,
      fields: {
        isPinned: pinned,
        viewerCanPin: !pinned,
        viewerCanUnpin: pinned,
        pinnedBy: pinned ? pinnedIssueComment?.pinnedBy : null,
      },
      pinning: {execute},
    }
    mutateIssue({operation: issueOperation})
    mutateComment({operation: commentOperation})
  }
}

export function usePinnedComment(options: PinnedCommentMutationOptions) {
  const {addToast} = useToastContext()
  const handlePinError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({type: 'error', message: PIN_ERROR})
  }, [addToast])
  const handleUnpinError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({type: 'error', message: UNPIN_ERROR})
  }, [addToast])

  const pinComment = useCommentPinningMutation(true, options, handlePinError)
  const unpinComment = useCommentPinningMutation(false, options, handleUnpinError)

  return {pinComment, unpinComment}
}
